import { APIClient } from 'infra/APIClient'
import { CursorPaged, APIConfig } from 'types/definitions'
import {
  DocumentDTO,
  DocumentPopulate,
  FindDocumentsQuery,
  CreateDocumentPayload,
  UpdateDocumentPayload,
  ZipFilesPayload,
  AssignDocumentTypePayload,
  SearchDocumentsQuery,
} from './dtos/document'
import {
  DocumentTypeDTO,
  GetManyDocumentTypesPayload,
} from './dtos/document-type'
import {
  DocumentTagDTO,
  DocumentTagsQuery,
  CreateTagPayload,
  UpdateTagPayload,
} from './dtos/document-tag'
import {
  CreateDocumentAccessPayload,
  UpdateDocumentAccessPayload,
} from './dtos/document-access'
import {
  DocumentVersionsQuery,
  CreateDocumentVersionPayload,
  DocumentVersionDTO,
  DocumentVersionQuery,
} from './dtos/document-version'
import { AxiosRequestConfig } from 'axios'
import { ConfirmDocumentPayload } from './dtos/client-data-verification'

export interface DocumentAPI {
  documents: DocumentsAPI
  access: DocumentAccessAPI
  version: DocumentVersionAPI
  tags: DocumentTagAPI
  types: DocumentTypeAPI
  clientDataVerification: ClientDataVerificationAPI
}

export function createDocumentAPI(opts: APIConfig): DocumentAPI {
  return {
    documents: new DocumentsAPI(opts),
    access: new DocumentAccessAPI(opts),
    version: new DocumentVersionAPI(opts),
    tags: new DocumentTagAPI(opts),
    types: new DocumentTypeAPI(opts),
    clientDataVerification: new ClientDataVerificationAPI(opts),
  }
}

export class DocumentsAPI extends APIClient {
  find(query: FindDocumentsQuery): Promise<CursorPaged<DocumentDTO>> {
    return this.client.get('/v3/documents', { params: query }).then(this._data)
  }

  search(query: SearchDocumentsQuery): Promise<CursorPaged<DocumentDTO>> {
    return this.client
      .get('/v3/documents/search', { params: query })
      .then(this._data)
  }

  get(longId: string, params?: DocumentPopulate): Promise<DocumentDTO | null> {
    return this.client
      .get(`/v3/documents/${longId}`, { params })
      .then(this._data)
  }

  create(data: CreateDocumentPayload): Promise<DocumentDTO> {
    return this.client.post('/v3/documents', data).then(this._data)
  }

  update(longId: string, data: UpdateDocumentPayload) {
    return this.client
      .patch(`/v3/documents/${longId}`, data, {
        params: { tags: true },
      })
      .then(this._data)
  }

  archive(longId: string): Promise<DocumentDTO> {
    return this.client
      .patch(
        `/v3/documents/${longId}/archive`,
        {},
        {
          params: { tags: true },
        },
      )
      .then(this._data)
  }

  zipFiles(data: ZipFilesPayload): Promise<DocumentDTO> {
    return this.client.post(`/v3/documents/zipFiles`, data).then(this._data)
  }

  getDownloadUrl(longId: string): Promise<{ url: string }> {
    return this.client.get(`/v3/documents/${longId}/download`).then(this._data)
  }

  assignType(id: string, data: AssignDocumentTypePayload) {
    return this.client
      .post(`/v3/documents/${id}/assign-type`, data)
      .then(this._data)
  }
}

export class DocumentTypeAPI extends APIClient {
  find(): Promise<DocumentTypeDTO[]> {
    return this.client.get('/v3/types').then(this._data)
  }

  get(id: string): Promise<DocumentTypeDTO> {
    return this.client.get(`/v3/types/${id}`).then(this._data)
  }

  getMany(
    ids: GetManyDocumentTypesPayload,
  ): Promise<Array<DocumentTypeDTO | null>> {
    return this.client.post(`/v3/types/id-query`, ids).then(this._data)
  }
}

export class DocumentTagAPI extends APIClient {
  find(query: DocumentTagsQuery): Promise<CursorPaged<DocumentTagDTO>> {
    return this.client.get('/v3/tags', { params: query }).then(this._data)
  }

  create(data: CreateTagPayload): Promise<DocumentDTO> {
    return this.client.post('/v3/tags', data).then(this._data)
  }

  update(id: string, data: UpdateTagPayload): Promise<DocumentDTO> {
    return this.client.patch(`/v3/tags/${id}`, data).then(this._data)
  }

  delete(id: string): Promise<void> {
    return this.client.delete(`/v3/tags/${id}`).then(this._data)
  }
}

export class DocumentAccessAPI extends APIClient {
  create(data: CreateDocumentAccessPayload) {
    return this.client.post('/v3/accesses', data).then(this._data)
  }

  update(id: string, data: UpdateDocumentAccessPayload) {
    return this.client.patch(`/v3/accesses/${id}`, data).then(this._data)
  }

  delete(id: string) {
    return this.client.delete(`/v3/accesses/${id}`).then(this._data)
  }
}

export class DocumentVersionAPI extends APIClient {
  get({
    document_id: documentLongId,
    version_id: versionId,
  }: DocumentVersionQuery) {
    return this.client
      .get(`/v3/documents/${documentLongId}/versions/${versionId}`)
      .then(this._data)
  }

  find({ document_id: documentLongId, ...query }: DocumentVersionsQuery) {
    return this.client
      .get(`/v3/documents/${documentLongId}/versions`, { params: query })
      .then(this._data)
  }

  create(
    { document_long_id: documentLongId, ...data }: CreateDocumentVersionPayload,
    opts: CreateOpts,
  ): Promise<DocumentVersionDTO> {
    const reqOpts: AxiosRequestConfig = {}
    let body: any = data
    if (data.action.type === 'ReplaceContents') {
      body = new FormData()
      const { files, ...action } = data.action
      body.append('meta', JSON.stringify({ action, message: data.message }))
      files.forEach((f, i) =>
        body.append(`file${i + 1}`, f, sanitizeFileName(f.name)),
      )
      reqOpts.onUploadProgress = (e) => {
        opts.onProgress({
          loaded: e.loaded,
          total: e.total ?? e.loaded,
        })
      }
    }

    return this.client
      .post(`/v3/documents/${documentLongId}/versions`, body, reqOpts)
      .then(this._data)
  }

}

export class ClientDataVerificationAPI extends APIClient {
  confirm(payload: ConfirmDocumentPayload) {
    return this.client
      .post(
        `/v3/documents/${payload.document_long_id}/versions/${payload.version_id}/files/${payload.file_id}/confirm`,
        {
          job_id: payload.job_id,
        },
      )
      .then(this._data)
  }
}

/**
 * Sanitizes a file name.
 * @param {string} str
 */
function sanitizeFileName(str: string) {
  return str.replace(/\//g, '_').replace(/\\/g, '_')
}

export interface CreateOpts {
  onProgress(e: UploadProgress): void
}

export interface UploadProgress {
  total: number
  loaded: number
}
